import { useState, useRef, useCallback } from 'react';
import { message, Modal, Button } from 'antd';
import { LoadingOutlined, ImportOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';

import request from '@/request';
import SelectorItem from './SelectorItem';
import UploadItem from './UploadItem';

export default function ImportButton({
  api,
  importEventData = false,
  onSuccess,
  demoDownloadUrl = '',
}) {
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedItemID, setSelectedItemID] = useState(null);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);
  const { event } = useSelector((state) => state.event);
  const fileInputRef = useRef(null);

  const handleButtonClick = () => {
    if (importEventData) {
      setModalVisible(true);
      setSelectedItemID(null);
      setUploadedFile(null);
    } else {
      setUploadedFile(null);
      fileInputRef.current?.click();
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];

    if (!file) return;

    // 验证文件类型
    const validTypes = [
      'application/vnd.ms-excel', // .xls
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
    ];

    if (!validTypes.includes(file.type) && !file.name.match(/\.(xls|xlsx)$/i)) {
      message.error('请选择 Excel 文件（.xls 或 .xlsx）');
      e.target.value = ''; // 清空选择
      return;
    }

    // 上传文件
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      await request.post(api, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      message.success('导入成功');
      onSuccess?.();
    } catch (error) {
      console.error('请求失败:', error);
    } finally {
      setLoading(false);
      // 清空 input，允许重复选择同一文件
      e.target.value = '';
    }
  };

  const handleModalConfirm = useCallback(async () => {
    if (!selectedItemID || !uploadedFile) {
      message.error('请选择赛项和文件');
      return;
    }
    setModalLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', uploadedFile);

      await request.post(`${api}&itemId=${selectedItemID}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      message.success('导入成功');
      setModalVisible(false);
      setUploadedFile(null);
      onSuccess?.();
    } catch (error) {
      console.error('请求失败:', error);
      message.error('导入失败');
    } finally {
      setModalLoading(false);
    }
  }, [api, selectedItemID, uploadedFile, onSuccess]);

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept=".xls,.xlsx,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        onChange={handleFileChange}
        className="hidden"
      />
      <button
        type="button"
        aria-label="导入"
        onClick={handleButtonClick}
        className={`h-9 flex items-center justify-center gap-2 px-4 text-sm font-semibold text-white bg-amber-400 rounded-lg hover:bg-amber-500 transition-colors ${loading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
      >
        {loading ? <LoadingOutlined /> : <ImportOutlined />}
        <span>导入</span>
      </button>

      {importEventData && (
        <Modal
          title="导入数据"
          open={modalVisible}
          onCancel={() => setModalVisible(false)}
          footer={[
            <Button key="cancel" onClick={() => setModalVisible(false)}>
              取消
            </Button>,
            <Button
              key="submit"
              type="primary"
              loading={modalLoading}
              disabled={!selectedItemID || !uploadedFile}
              onClick={handleModalConfirm}
            >
              确认
            </Button>,
          ]}
          width={640}
        >
          <div className="space-y-6 py-4">
            {demoDownloadUrl && (
              <div className="flex items-center gap-4">
                <label className="block text-sm font-medium text-slate-700 ml-2">
                  模板
                </label>
                <a
                  href={demoDownloadUrl}
                  className="h-8  px-4 text-sm/8 font-medium hover:bg-blue-600 cursor-pointer"
                >
                  {decodeURIComponent(demoDownloadUrl).split('/').pop()}
                </a>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                <span className="text-red-500 mr-1">*</span>
                选择赛项
              </label>
              <SelectorItem
                options={
                  event?.items?.map((i) => ({
                    label: i.name + '-' + i.groupName,
                    value: i.id,
                  })) || []
                }
                value={selectedItemID}
                onChange={setSelectedItemID}
                placeholder="请选择赛项"
              />
            </div>

            <UploadItem
              key={modalVisible ? 'upload' : 'upload-closed'}
              label="选择文件"
              require
              accept=".xls,.xlsx"
              multiple={false}
              value={uploadedFile ? [uploadedFile] : []}
              beforeUpload={(file) => {
                setUploadedFile(file);
                // 阻止自动上传
                return false;
              }}
            />
          </div>
        </Modal>
      )}
    </>
  );
}
